import SelectInput from '@/components/Input/SelectInput';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLanguage } from '@/context/LanguageContext';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/es';
import {
  StatusFilterGroup,
  StatusToggleButton,
} from '@/pages/ListEquipments/styles';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import TextField from '@/components/Input/TextField';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/** Empty state per field is `''` for select/toggle and `null` for date. */
export type FilterValue = string | string[] | null;

interface SelectProps {
  multiple?: boolean;
  searchable?: boolean;
  groupedOptions?: { group: string; items: string[] }[];
  startIcon?: React.ReactNode;
}

interface DateProps {
  disableFuture?: boolean;
  disablePast?: boolean;
}

interface ToggleProps {
  startIcons?: (IconName | null)[];
  endIcons?: (IconName | null)[];
}
interface TextProps {
  placeholder?: string;
}

interface FilterOptionProps {
  field: 'text' | 'toggle' | 'select' | 'date';
  name: string;
  label: string | string[];
  // Injected by FiltersDisplay; only needed when used on its own
  value?: FilterValue;
  options?: string[];
  setValue?: (name: string, value: FilterValue) => void;
  selectProps?: SelectProps;
  dateProps?: DateProps;
  toggleProps?: ToggleProps;
  textProps?: TextProps;
  disabled?: boolean;
  width?: string;
}

const locales = { en: 'en', pt: 'pt-br', es: 'es' };
const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

function FilterOption({
  field,
  name,
  label,
  value,
  options = [],
  setValue,
  selectProps = {
    multiple: false,
    searchable: false,
    groupedOptions: undefined,
    startIcon: undefined,
  },
  dateProps = {
    disableFuture: false,
    disablePast: false,
  },
  toggleProps = {
    startIcons: [],
    endIcons: [],
  },
  textProps = {
    placeholder: '',
  },
  disabled = false,
  width,
}: FilterOptionProps) {
  const { language } = useLanguage();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const dynamicWidth = (width = '100%') => {
    return isSm ? '100%' : width;
  };

  return (
    <>
      {field === 'select' ? (
        <SelectInput
          id={name}
          name={name}
          label={label as string}
          value={value ?? (selectProps?.multiple ? [] : '')}
          options={options}
          onChange={(event) => setValue?.(name, event.target.value)}
          multiple={selectProps?.multiple}
          searchable={selectProps?.searchable}
          disabled={disabled}
          groupedOptions={selectProps?.groupedOptions}
          startIcon={selectProps?.startIcon}
          width={dynamicWidth(width)}
        />
      ) : field === 'text' ? (
        <TextField
          id={name}
          name={name}
          label={label as string}
          value={value ?? ''}
          onChange={(event) => setValue?.(name, event.target.value)}
          disabled={disabled}
          placeholder={textProps?.placeholder}
          width={dynamicWidth(width)}
        />
      ) : field === 'date' ? (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={locales[language as keyof typeof locales]}
        >
          <DateTimePicker
            ampm={false}
            name={name}
            label={label as string}
            value={value ? dayjs(value as string, DATE_FORMAT) : null}
            onChange={(newValue) =>
              setValue?.(name, newValue ? newValue.format(DATE_FORMAT) : null)
            }
            disabled={disabled}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            disableFuture={dateProps?.disableFuture}
            disablePast={dateProps?.disablePast}
          />
        </LocalizationProvider>
      ) : field === 'toggle' ? (
        <StatusFilterGroup
          value={value || null}
          exclusive
          disabled={disabled}
          onChange={(_: unknown, newValue: string | null) =>
            setValue?.(name, newValue)
          }
          // Sized to its buttons unless a width is given or on small screens
          sx={{ width: isSm ? '100%' : width }}
        >
          {options.map((option, index) => {
            const startIcon = toggleProps.startIcons?.[index];
            const endIcon = toggleProps.endIcons?.[index];
            return (
              <StatusToggleButton
                value={option}
                key={index}
                sx={{ flex: isSm || width ? 1 : undefined }}
              >
                {startIcon ? (
                  <DynamicIcon
                    name={startIcon}
                    size={14}
                    style={{ marginRight: 6 }}
                  />
                ) : null}
                {label[index]}
                {endIcon ? (
                  <DynamicIcon
                    name={endIcon}
                    size={14}
                    style={{ marginLeft: 6 }}
                  />
                ) : null}
              </StatusToggleButton>
            );
          })}
        </StatusFilterGroup>
      ) : null}
    </>
  );
}

export default FilterOption;
